import { toSentenceCase } from "common/strings";

// A generated document's warnings are either values that printed as blank or,
// for an endorsable document, an endorsement that could not be generated and
// was replaced by an error page (source "endorsement"). The second is a missing
// page rather than a missing value, so the heading has to say so.
const isReplacedEndorsement = (warning) => warning?.source === "endorsement";

export const outcomeWarningsTitle = (warnings) =>
  (warnings ?? []).some(isReplacedEndorsement)
    ? "Generated with errors"
    : "Generated with blanks";

const withDetail = (warning) =>
  [warning.message, warning.detail].filter(Boolean).join(" ");

// The outcome's warnings as one summary and the list of everything behind it.
// A replaced endorsement leads, as the more serious problem; its message is
// the summary, so its bullet only gives the reason.
export const outcomeWarningsSummary = (warnings) => {
  const all = warnings ?? [];
  const replaced = all.filter(isReplacedEndorsement);
  const blanks = all.filter((warning) => !isReplacedEndorsement(warning));
  const blankDetails = blanks.map(withDetail);

  if (replaced.length === 1) {
    return {
      message: replaced[0].message,
      details: [...blankDetails, replaced[0].detail],
    };
  }
  if (replaced.length > 1) {
    return {
      message: `${replaced.length} endorsements could not be generated and were replaced by error pages.`,
      details: [
        ...blankDetails,
        ...replaced.map((warning) =>
          [warning.templateTitle ?? warning.name, warning.detail]
            .filter(Boolean)
            .join(": ")
        ),
      ],
    };
  }
  if (blanks.length === 1) {
    return { message: blanks[0].message, details: [blanks[0].detail] };
  }
  return {
    message: `${blanks.length} values were not found and printed as blank.`,
    details: blankDetails,
  };
};

export const outcomeErrorTitle = (error) =>
  error?.code ? toSentenceCase(error.code) : "Generation failed";

// Templates the summary could not evaluate. `templateErrors` describes those
// that failed to render; `erroringTemplates` names those too, plus any whose
// tags could not be read at all, which have no description, so those are
// listed by name with a generic message.
export const summaryTemplateErrors = (data) => {
  const described = data?.templateErrors ?? [];
  const describedTitles = new Set(
    described.map((entry) => entry.templateTitle)
  );
  const undescribed = (data?.erroringTemplates ?? [])
    .filter((name) => !describedTitles.has(name))
    .map((name) => ({
      templateTitle: name,
      message: "This template could not be evaluated.",
    }));
  return [...described, ...undescribed];
};
